/**
 * Room shape templates for the room-picker wizard.
 *
 * Each shape is defined by named parameters in millimetres (mm). Vertices and
 * walls are derived from those parameters so the polygon always stays closed
 * when the user edits a wall length.
 *
 * Wall-edit policy (`applyWallLength`): lengthening a segment should grow the
 * related outer envelope while keeping sibling segments stable. Do not shrink
 * other walls just to keep the previous outer size.
 */

export type RoomShapeId =
  | "rectangle"
  | "square"
  | "lShape"
  | "tShape"
  | "uShape"
  | "corridor"
  | "zShape"
  | "extendedCorner"
  | "cutCorner"
  | "chamfered"
  | "slantedWall"
  | "trapezoid"
  | "pentagon"
  | "alcove"
  | "inset";

export const ROOM_SHAPE_IDS: RoomShapeId[] = [
  "rectangle",
  "square",
  "lShape",
  "tShape",
  "uShape",
  "corridor",
  "zShape",
  "extendedCorner",
  "cutCorner",
  "chamfered",
  "slantedWall",
  "trapezoid",
  "pentagon",
  "alcove",
  "inset",
];

export type RoomShapeParams = Record<string, number>;

export interface RoomWallSegment {
  /** Stable wall index within the shape (0..n-1, CCW from bottom-left). */
  index: number;
  /** Parameter key that primarily drives this wall's length. */
  paramKey: string;
  /**
   * How the wall length relates to the parameter.
   * - `direct`: length = param
   * - `diagonal`: length = param * √2 (45° chamfer)
   * - `derived`: length is computed from multiple params
   */
  lengthMode: "direct" | "diagonal" | "derived";
}

export interface ApplyWallLengthResult {
  params: RoomShapeParams;
  /** Length actually applied after clamping (mm). */
  appliedLengthMm: number;
  /** Minimum allowed length for this wall given current params (mm). */
  minLengthMm: number;
  wasClamped: boolean;
}

export interface RoomShapeDefinition {
  id: RoomShapeId;
  /** i18n key under roomPicker.shapes.* */
  labelKey: string;
  defaultParams: RoomShapeParams;
  /** Minimum allowed value per parameter (mm). */
  minParams: RoomShapeParams;
  walls: RoomWallSegment[];
  /** Build closed polygon vertices in mm, CCW, starting near bottom-left. */
  getVertices: (params: RoomShapeParams) => Array<{ x: number; y: number }>;
  /**
   * Apply a typed wall length (mm) back onto params while keeping the polygon valid.
   */
  applyWallLength: (
    params: RoomShapeParams,
    wallIndex: number,
    lengthMm: number,
  ) => ApplyWallLengthResult;
  /** Minimum editable length for a wall given current params (mm). */
  getMinWallLengthMm: (params: RoomShapeParams, wallIndex: number) => number;
}

const SQRT2 = Math.SQRT2;

/** Small clearance between related segments so the shape stays valid (mm). */
const CLEARANCE_MM = 400;

const clamp = (value: number, min: number): number =>
  Math.max(min, Math.round(value));

/**
 * Distance between consecutive vertices (last closes to first).
 */
export const wallLengthsFromVertices = (
  vertices: Array<{ x: number; y: number }>,
): number[] => {
  const lengths: number[] = [];
  for (let i = 0; i < vertices.length; i++) {
    const a = vertices[i];
    const b = vertices[(i + 1) % vertices.length];
    lengths.push(Math.hypot(b.x - a.x, b.y - a.y));
  }
  return lengths;
};

const finishApply = (
  definition: RoomShapeDefinition,
  nextParams: RoomShapeParams,
  wallIndex: number,
  requestedMm: number,
  minLengthMm: number,
): ApplyWallLengthResult => {
  const appliedLengthMm = wallLengthsFromVertices(
    definition.getVertices(nextParams),
  )[wallIndex];
  return {
    params: nextParams,
    appliedLengthMm,
    minLengthMm,
    wasClamped: requestedMm + 0.5 < minLengthMm,
  };
};

export const ROOM_SHAPE_DEFINITIONS: Record<RoomShapeId, RoomShapeDefinition> = {
  rectangle: {
    id: "rectangle",
    labelKey: "rectangle",
    defaultParams: { width: 4000, height: 3000 },
    minParams: { width: 1000, height: 1000 },
    walls: [
      { index: 0, paramKey: "width", lengthMode: "direct" },
      { index: 1, paramKey: "height", lengthMode: "direct" },
      { index: 2, paramKey: "width", lengthMode: "direct" },
      { index: 3, paramKey: "height", lengthMode: "direct" },
    ],
    getVertices: ({ width, height }) => [
      { x: 0, y: 0 },
      { x: width, y: 0 },
      { x: width, y: height },
      { x: 0, y: height },
    ],
    getMinWallLengthMm: (_params, wallIndex) => {
      const min = ROOM_SHAPE_DEFINITIONS.rectangle.minParams;
      return wallIndex === 0 || wallIndex === 2 ? min.width : min.height;
    },
    applyWallLength: (params, wallIndex, lengthMm) => {
      const def = ROOM_SHAPE_DEFINITIONS.rectangle;
      const minLengthMm = def.getMinWallLengthMm(params, wallIndex);
      const next = { ...params };
      if (wallIndex === 0 || wallIndex === 2) {
        next.width = clamp(lengthMm, minLengthMm);
      } else {
        next.height = clamp(lengthMm, minLengthMm);
      }
      return finishApply(def, next, wallIndex, lengthMm, minLengthMm);
    },
  },

  square: {
    id: "square",
    labelKey: "square",
    defaultParams: { size: 4000 },
    minParams: { size: 1000 },
    walls: [
      { index: 0, paramKey: "size", lengthMode: "direct" },
      { index: 1, paramKey: "size", lengthMode: "direct" },
      { index: 2, paramKey: "size", lengthMode: "direct" },
      { index: 3, paramKey: "size", lengthMode: "direct" },
    ],
    getVertices: ({ size }) => [
      { x: 0, y: 0 },
      { x: size, y: 0 },
      { x: size, y: size },
      { x: 0, y: size },
    ],
    getMinWallLengthMm: () => ROOM_SHAPE_DEFINITIONS.square.minParams.size,
    applyWallLength: (params, wallIndex, lengthMm) => {
      const def = ROOM_SHAPE_DEFINITIONS.square;
      const minLengthMm = def.getMinWallLengthMm(params, wallIndex);
      const next = { ...params, size: clamp(lengthMm, minLengthMm) };
      return finishApply(def, next, wallIndex, lengthMm, minLengthMm);
    },
  },

  lShape: {
    id: "lShape",
    labelKey: "lShape",
    defaultParams: {
      outerWidth: 5000,
      outerHeight: 4000,
      armWidth: 2000,
      stemHeight: 2000,
    },
    minParams: {
      outerWidth: 2000,
      outerHeight: 2000,
      armWidth: 800,
      stemHeight: 800,
    },
    walls: [
      { index: 0, paramKey: "outerWidth", lengthMode: "direct" },
      { index: 1, paramKey: "stemHeight", lengthMode: "direct" },
      { index: 2, paramKey: "derived", lengthMode: "derived" },
      { index: 3, paramKey: "derived", lengthMode: "derived" },
      { index: 4, paramKey: "armWidth", lengthMode: "direct" },
      { index: 5, paramKey: "outerHeight", lengthMode: "direct" },
    ],
    getVertices: ({ outerWidth, outerHeight, armWidth, stemHeight }) => [
      { x: 0, y: 0 },
      { x: outerWidth, y: 0 },
      { x: outerWidth, y: stemHeight },
      { x: armWidth, y: stemHeight },
      { x: armWidth, y: outerHeight },
      { x: 0, y: outerHeight },
    ],
    getMinWallLengthMm: (params, wallIndex) => {
      const min = ROOM_SHAPE_DEFINITIONS.lShape.minParams;
      switch (wallIndex) {
        case 0:
          return Math.max(min.outerWidth, params.armWidth + CLEARANCE_MM);
        case 1:
          return min.stemHeight;
        case 2:
          return CLEARANCE_MM;
        case 3:
          return CLEARANCE_MM;
        case 4:
          return min.armWidth;
        case 5:
          return Math.max(min.outerHeight, params.stemHeight + CLEARANCE_MM);
        default:
          return CLEARANCE_MM;
      }
    },
    applyWallLength: (params, wallIndex, lengthMm) => {
      const def = ROOM_SHAPE_DEFINITIONS.lShape;
      const min = def.minParams;
      const minLengthMm = def.getMinWallLengthMm(params, wallIndex);
      const next = { ...params };
      switch (wallIndex) {
        case 0:
          next.outerWidth = clamp(lengthMm, minLengthMm);
          break;
        case 1:
          next.stemHeight = clamp(lengthMm, min.stemHeight);
          // Grow outer height instead of clamping stem down.
          if (next.stemHeight > next.outerHeight - CLEARANCE_MM) {
            next.outerHeight = next.stemHeight + CLEARANCE_MM;
          }
          break;
        case 2: {
          // Inner horizontal: keep armWidth, grow outerWidth.
          const inner = clamp(lengthMm, CLEARANCE_MM);
          next.outerWidth = clamp(next.armWidth + inner, min.outerWidth);
          break;
        }
        case 3: {
          // Inner vertical: keep stemHeight, grow outerHeight.
          const inner = clamp(lengthMm, CLEARANCE_MM);
          next.outerHeight = clamp(next.stemHeight + inner, min.outerHeight);
          break;
        }
        case 4:
          next.armWidth = clamp(lengthMm, min.armWidth);
          if (next.armWidth > next.outerWidth - CLEARANCE_MM) {
            next.outerWidth = next.armWidth + CLEARANCE_MM;
          }
          break;
        case 5:
          next.outerHeight = clamp(lengthMm, minLengthMm);
          break;
        default:
          break;
      }
      return finishApply(def, next, wallIndex, lengthMm, minLengthMm);
    },
  },

  cutCorner: {
    id: "cutCorner",
    labelKey: "cutCorner",
    defaultParams: { width: 4000, height: 3000, cut: 1000 },
    minParams: { width: 2000, height: 2000, cut: 400 },
    walls: [
      { index: 0, paramKey: "width", lengthMode: "direct" },
      { index: 1, paramKey: "derived", lengthMode: "derived" },
      { index: 2, paramKey: "cut", lengthMode: "diagonal" },
      { index: 3, paramKey: "derived", lengthMode: "derived" },
      { index: 4, paramKey: "height", lengthMode: "direct" },
    ],
    getVertices: ({ width, height, cut }) => [
      { x: 0, y: 0 },
      { x: width, y: 0 },
      { x: width, y: height - cut },
      { x: width - cut, y: height },
      { x: 0, y: height },
    ],
    getMinWallLengthMm: (params, wallIndex) => {
      const min = ROOM_SHAPE_DEFINITIONS.cutCorner.minParams;
      switch (wallIndex) {
        case 0:
          return Math.max(min.width, params.cut + CLEARANCE_MM * 2);
        case 1:
        case 3:
          return CLEARANCE_MM;
        case 2:
          return min.cut * SQRT2;
        case 4:
          return Math.max(min.height, params.cut + CLEARANCE_MM * 2);
        default:
          return CLEARANCE_MM;
      }
    },
    applyWallLength: (params, wallIndex, lengthMm) => {
      const def = ROOM_SHAPE_DEFINITIONS.cutCorner;
      const min = def.minParams;
      const minLengthMm = def.getMinWallLengthMm(params, wallIndex);
      const next = { ...params };
      switch (wallIndex) {
        case 0:
          next.width = clamp(lengthMm, minLengthMm);
          break;
        case 1: {
          // Vertical remainder: keep cut, grow height.
          const vertical = clamp(lengthMm, CLEARANCE_MM);
          next.height = clamp(vertical + next.cut, min.height);
          break;
        }
        case 2: {
          next.cut = clamp(lengthMm / SQRT2, min.cut);
          const needed = next.cut + CLEARANCE_MM * 2;
          if (next.width < needed) next.width = needed;
          if (next.height < needed) next.height = needed;
          break;
        }
        case 3: {
          // Horizontal remainder: keep cut, grow width.
          const horizontal = clamp(lengthMm, CLEARANCE_MM);
          next.width = clamp(horizontal + next.cut, min.width);
          break;
        }
        case 4:
          next.height = clamp(lengthMm, minLengthMm);
          break;
        default:
          break;
      }
      return finishApply(def, next, wallIndex, lengthMm, minLengthMm);
    },
  },

  tShape: {
    id: "tShape",
    labelKey: "tShape",
    defaultParams: {
      totalWidth: 5000,
      totalHeight: 4000,
      stemWidth: 1800,
      stemHeight: 1800,
    },
    minParams: {
      totalWidth: 2400,
      totalHeight: 2400,
      stemWidth: 800,
      stemHeight: 800,
    },
    walls: [
      { index: 0, paramKey: "stemWidth", lengthMode: "direct" },
      { index: 1, paramKey: "stemHeight", lengthMode: "direct" },
      { index: 2, paramKey: "derived", lengthMode: "derived" },
      { index: 3, paramKey: "derived", lengthMode: "derived" },
      { index: 4, paramKey: "totalWidth", lengthMode: "direct" },
      { index: 5, paramKey: "derived", lengthMode: "derived" },
      { index: 6, paramKey: "derived", lengthMode: "derived" },
      { index: 7, paramKey: "stemHeight", lengthMode: "direct" },
    ],
    getVertices: ({ totalWidth, totalHeight, stemWidth, stemHeight }) => {
      const armInset = (totalWidth - stemWidth) / 2;
      return [
        { x: armInset, y: 0 },
        { x: armInset + stemWidth, y: 0 },
        { x: armInset + stemWidth, y: stemHeight },
        { x: totalWidth, y: stemHeight },
        { x: totalWidth, y: totalHeight },
        { x: 0, y: totalHeight },
        { x: 0, y: stemHeight },
        { x: armInset, y: stemHeight },
      ];
    },
    getMinWallLengthMm: (params, wallIndex) => {
      const min = ROOM_SHAPE_DEFINITIONS.tShape.minParams;
      switch (wallIndex) {
        case 0:
          return min.stemWidth;
        case 1:
        case 7:
          return min.stemHeight;
        case 2:
        case 6:
          return CLEARANCE_MM;
        case 3:
        case 5:
          return CLEARANCE_MM;
        case 4:
          return Math.max(min.totalWidth, params.stemWidth + CLEARANCE_MM * 2);
        default:
          return CLEARANCE_MM;
      }
    },
    applyWallLength: (params, wallIndex, lengthMm) => {
      const def = ROOM_SHAPE_DEFINITIONS.tShape;
      const min = def.minParams;
      const minLengthMm = def.getMinWallLengthMm(params, wallIndex);
      const next = { ...params };
      switch (wallIndex) {
        case 0:
          next.stemWidth = clamp(lengthMm, min.stemWidth);
          if (next.stemWidth > next.totalWidth - CLEARANCE_MM * 2) {
            next.totalWidth = next.stemWidth + CLEARANCE_MM * 2;
          }
          break;
        case 1:
        case 7:
          next.stemHeight = clamp(lengthMm, min.stemHeight);
          if (next.stemHeight > next.totalHeight - CLEARANCE_MM * 2) {
            next.totalHeight = next.stemHeight + CLEARANCE_MM * 2;
          }
          break;
        case 2:
        case 6: {
          const wing = clamp(lengthMm, CLEARANCE_MM);
          next.totalWidth = clamp(next.stemWidth + wing * 2, min.totalWidth);
          break;
        }
        case 3:
        case 5: {
          const bar = clamp(lengthMm, CLEARANCE_MM);
          next.totalHeight = clamp(next.stemHeight + bar, min.totalHeight);
          break;
        }
        case 4:
          next.totalWidth = clamp(lengthMm, minLengthMm);
          break;
        default:
          break;
      }
      return finishApply(def, next, wallIndex, lengthMm, minLengthMm);
    },
  },

  uShape: {
    id: "uShape",
    labelKey: "uShape",
    defaultParams: {
      totalWidth: 5000,
      totalHeight: 4000,
      armWidth: 1400,
      notchDepth: 2200,
    },
    minParams: {
      totalWidth: 2400,
      totalHeight: 2000,
      armWidth: 600,
      notchDepth: 600,
    },
    walls: [
      { index: 0, paramKey: "totalWidth", lengthMode: "direct" },
      { index: 1, paramKey: "totalHeight", lengthMode: "direct" },
      { index: 2, paramKey: "armWidth", lengthMode: "direct" },
      { index: 3, paramKey: "notchDepth", lengthMode: "direct" },
      { index: 4, paramKey: "derived", lengthMode: "derived" },
      { index: 5, paramKey: "notchDepth", lengthMode: "direct" },
      { index: 6, paramKey: "armWidth", lengthMode: "direct" },
      { index: 7, paramKey: "totalHeight", lengthMode: "direct" },
    ],
    getVertices: ({ totalWidth, totalHeight, armWidth, notchDepth }) => [
      { x: 0, y: 0 },
      { x: totalWidth, y: 0 },
      { x: totalWidth, y: totalHeight },
      { x: totalWidth - armWidth, y: totalHeight },
      { x: totalWidth - armWidth, y: totalHeight - notchDepth },
      { x: armWidth, y: totalHeight - notchDepth },
      { x: armWidth, y: totalHeight },
      { x: 0, y: totalHeight },
    ],
    getMinWallLengthMm: (params, wallIndex) => {
      const min = ROOM_SHAPE_DEFINITIONS.uShape.minParams;
      switch (wallIndex) {
        case 0:
          return Math.max(min.totalWidth, params.armWidth * 2 + CLEARANCE_MM);
        case 1:
        case 7:
          return Math.max(min.totalHeight, params.notchDepth + CLEARANCE_MM);
        case 2:
        case 6:
          return min.armWidth;
        case 3:
        case 5:
          return min.notchDepth;
        case 4:
          return CLEARANCE_MM;
        default:
          return CLEARANCE_MM;
      }
    },
    applyWallLength: (params, wallIndex, lengthMm) => {
      const def = ROOM_SHAPE_DEFINITIONS.uShape;
      const min = def.minParams;
      const minLengthMm = def.getMinWallLengthMm(params, wallIndex);
      const next = { ...params };
      switch (wallIndex) {
        case 0:
          next.totalWidth = clamp(lengthMm, minLengthMm);
          break;
        case 1:
        case 7:
          next.totalHeight = clamp(lengthMm, minLengthMm);
          break;
        case 2:
        case 6:
          next.armWidth = clamp(lengthMm, min.armWidth);
          if (next.armWidth * 2 > next.totalWidth - CLEARANCE_MM) {
            next.totalWidth = next.armWidth * 2 + CLEARANCE_MM;
          }
          break;
        case 3:
        case 5:
          next.notchDepth = clamp(lengthMm, min.notchDepth);
          if (next.notchDepth > next.totalHeight - CLEARANCE_MM) {
            next.totalHeight = next.notchDepth + CLEARANCE_MM;
          }
          break;
        case 4: {
          // Notch floor: keep arm widths, grow totalWidth.
          const floorLen = clamp(lengthMm, CLEARANCE_MM);
          next.totalWidth = clamp(
            next.armWidth * 2 + floorLen,
            min.totalWidth,
          );
          break;
        }
        default:
          break;
      }
      return finishApply(def, next, wallIndex, lengthMm, minLengthMm);
    },
  },

  chamfered: {
    id: "chamfered",
    labelKey: "chamfered",
    defaultParams: { width: 4000, height: 3000, chamfer: 800 },
    minParams: { width: 2000, height: 2000, chamfer: 400 },
    walls: [
      { index: 0, paramKey: "width", lengthMode: "direct" },
      { index: 1, paramKey: "derived", lengthMode: "derived" },
      { index: 2, paramKey: "chamfer", lengthMode: "diagonal" },
      { index: 3, paramKey: "derived", lengthMode: "derived" },
      { index: 4, paramKey: "chamfer", lengthMode: "diagonal" },
      { index: 5, paramKey: "derived", lengthMode: "derived" },
    ],
    getVertices: ({ width, height, chamfer }) => [
      { x: 0, y: 0 },
      { x: width, y: 0 },
      { x: width, y: height - chamfer },
      { x: width - chamfer, y: height },
      { x: chamfer, y: height },
      { x: 0, y: height - chamfer },
    ],
    getMinWallLengthMm: (params, wallIndex) => {
      const min = ROOM_SHAPE_DEFINITIONS.chamfered.minParams;
      switch (wallIndex) {
        case 0:
          return Math.max(min.width, params.chamfer * 2 + CLEARANCE_MM * 2);
        case 1:
        case 5:
          return CLEARANCE_MM;
        case 2:
        case 4:
          return min.chamfer * SQRT2;
        case 3:
          return CLEARANCE_MM;
        default:
          return CLEARANCE_MM;
      }
    },
    applyWallLength: (params, wallIndex, lengthMm) => {
      const def = ROOM_SHAPE_DEFINITIONS.chamfered;
      const min = def.minParams;
      const minLengthMm = def.getMinWallLengthMm(params, wallIndex);
      const next = { ...params };
      switch (wallIndex) {
        case 0:
          next.width = clamp(lengthMm, minLengthMm);
          break;
        case 1:
        case 5: {
          // Vertical remainder: keep chamfer, grow height.
          const vertical = clamp(lengthMm, CLEARANCE_MM);
          next.height = clamp(vertical + next.chamfer, min.height);
          break;
        }
        case 2:
        case 4: {
          next.chamfer = clamp(lengthMm / SQRT2, min.chamfer);
          const neededWidth = next.chamfer * 2 + CLEARANCE_MM * 2;
          const neededHeight = next.chamfer + CLEARANCE_MM * 2;
          if (next.width < neededWidth) next.width = neededWidth;
          if (next.height < neededHeight) next.height = neededHeight;
          break;
        }
        case 3: {
          // Top flat: keep chamfer, grow width.
          const top = clamp(lengthMm, CLEARANCE_MM);
          next.width = clamp(top + next.chamfer * 2, min.width);
          break;
        }
        default:
          break;
      }
      return finishApply(def, next, wallIndex, lengthMm, minLengthMm);
    },
  },

  corridor: {
    id: "corridor",
    labelKey: "corridor",
    defaultParams: { width: 7000, height: 2000 },
    minParams: { width: 2000, height: 800 },
    walls: [
      { index: 0, paramKey: "width", lengthMode: "direct" },
      { index: 1, paramKey: "height", lengthMode: "direct" },
      { index: 2, paramKey: "width", lengthMode: "direct" },
      { index: 3, paramKey: "height", lengthMode: "direct" },
    ],
    getVertices: ({ width, height }) => [
      { x: 0, y: 0 },
      { x: width, y: 0 },
      { x: width, y: height },
      { x: 0, y: height },
    ],
    getMinWallLengthMm: (_params, wallIndex) => {
      const min = ROOM_SHAPE_DEFINITIONS.corridor.minParams;
      return wallIndex === 0 || wallIndex === 2 ? min.width : min.height;
    },
    applyWallLength: (params, wallIndex, lengthMm) => {
      const def = ROOM_SHAPE_DEFINITIONS.corridor;
      const minLengthMm = def.getMinWallLengthMm(params, wallIndex);
      const next = { ...params };
      if (wallIndex === 0 || wallIndex === 2) {
        next.width = clamp(lengthMm, minLengthMm);
      } else {
        next.height = clamp(lengthMm, minLengthMm);
      }
      return finishApply(def, next, wallIndex, lengthMm, minLengthMm);
    },
  },

  zShape: {
    id: "zShape",
    labelKey: "zShape",
    defaultParams: {
      totalWidth: 5000,
      totalHeight: 4500,
      armWidth: 2800,
      armHeight: 1500,
    },
    minParams: {
      totalWidth: 2400,
      totalHeight: 2400,
      armWidth: 1000,
      armHeight: 800,
    },
    walls: [
      { index: 0, paramKey: "armWidth", lengthMode: "direct" },
      { index: 1, paramKey: "armHeight", lengthMode: "direct" },
      { index: 2, paramKey: "derived", lengthMode: "derived" },
      { index: 3, paramKey: "derived", lengthMode: "derived" },
      { index: 4, paramKey: "armWidth", lengthMode: "direct" },
      { index: 5, paramKey: "armHeight", lengthMode: "direct" },
      { index: 6, paramKey: "derived", lengthMode: "derived" },
      { index: 7, paramKey: "derived", lengthMode: "derived" },
    ],
    getVertices: ({ totalWidth, totalHeight, armWidth, armHeight }) => [
      { x: 0, y: 0 },
      { x: armWidth, y: 0 },
      { x: armWidth, y: armHeight },
      { x: totalWidth, y: armHeight },
      { x: totalWidth, y: totalHeight },
      { x: totalWidth - armWidth, y: totalHeight },
      { x: totalWidth - armWidth, y: totalHeight - armHeight },
      { x: 0, y: totalHeight - armHeight },
    ],
    getMinWallLengthMm: (params, wallIndex) => {
      const min = ROOM_SHAPE_DEFINITIONS.zShape.minParams;
      switch (wallIndex) {
        case 0:
        case 4:
          return Math.max(min.armWidth, CLEARANCE_MM);
        case 1:
        case 5:
          return min.armHeight;
        case 2:
        case 6:
          return CLEARANCE_MM;
        case 3:
        case 7:
          return Math.max(
            min.totalHeight - params.armHeight,
            CLEARANCE_MM * 2,
          );
        default:
          return CLEARANCE_MM;
      }
    },
    applyWallLength: (params, wallIndex, lengthMm) => {
      const def = ROOM_SHAPE_DEFINITIONS.zShape;
      const min = def.minParams;
      const minLengthMm = def.getMinWallLengthMm(params, wallIndex);
      const next = { ...params };
      switch (wallIndex) {
        case 0:
        case 4:
          next.armWidth = clamp(lengthMm, min.armWidth);
          if (next.armWidth > next.totalWidth - CLEARANCE_MM) {
            next.totalWidth = next.armWidth + CLEARANCE_MM;
          }
          break;
        case 1:
        case 5:
          next.armHeight = clamp(lengthMm, min.armHeight);
          if (next.armHeight * 2 > next.totalHeight - CLEARANCE_MM) {
            next.totalHeight = next.armHeight * 2 + CLEARANCE_MM;
          }
          break;
        case 2:
        case 6: {
          const span = clamp(lengthMm, CLEARANCE_MM);
          next.totalWidth = clamp(next.armWidth + span, min.totalWidth);
          break;
        }
        case 3:
        case 7: {
          const mid = clamp(lengthMm, CLEARANCE_MM * 2);
          next.totalHeight = clamp(next.armHeight + mid, min.totalHeight);
          break;
        }
        default:
          break;
      }
      return finishApply(def, next, wallIndex, lengthMm, minLengthMm);
    },
  },

  extendedCorner: {
    id: "extendedCorner",
    labelKey: "extendedCorner",
    defaultParams: {
      width: 4000,
      height: 4000,
      protrusionDepth: 1500,
      protrusionHeight: 2000,
      protrusionOffset: 1000,
    },
    minParams: {
      width: 2000,
      height: 2000,
      protrusionDepth: 600,
      protrusionHeight: 800,
      protrusionOffset: 0,
    },
    walls: [
      { index: 0, paramKey: "width", lengthMode: "direct" },
      { index: 1, paramKey: "protrusionOffset", lengthMode: "direct" },
      { index: 2, paramKey: "protrusionDepth", lengthMode: "direct" },
      { index: 3, paramKey: "protrusionHeight", lengthMode: "direct" },
      { index: 4, paramKey: "protrusionDepth", lengthMode: "direct" },
      { index: 5, paramKey: "derived", lengthMode: "derived" },
      { index: 6, paramKey: "width", lengthMode: "direct" },
      { index: 7, paramKey: "height", lengthMode: "direct" },
    ],
    getVertices: ({
      width,
      height,
      protrusionDepth,
      protrusionHeight,
      protrusionOffset,
    }) => [
      { x: 0, y: 0 },
      { x: width, y: 0 },
      { x: width, y: protrusionOffset },
      { x: width + protrusionDepth, y: protrusionOffset },
      {
        x: width + protrusionDepth,
        y: protrusionOffset + protrusionHeight,
      },
      { x: width, y: protrusionOffset + protrusionHeight },
      { x: width, y: height },
      { x: 0, y: height },
    ],
    getMinWallLengthMm: (params, wallIndex) => {
      const min = ROOM_SHAPE_DEFINITIONS.extendedCorner.minParams;
      switch (wallIndex) {
        case 0:
        case 6:
          return min.width;
        case 1:
          return min.protrusionOffset;
        case 2:
        case 4:
          return min.protrusionDepth;
        case 3:
          return min.protrusionHeight;
        case 5:
          return CLEARANCE_MM;
        case 7:
          return Math.max(
            min.height,
            params.protrusionOffset + params.protrusionHeight + CLEARANCE_MM,
          );
        default:
          return CLEARANCE_MM;
      }
    },
    applyWallLength: (params, wallIndex, lengthMm) => {
      const def = ROOM_SHAPE_DEFINITIONS.extendedCorner;
      const min = def.minParams;
      const minLengthMm = def.getMinWallLengthMm(params, wallIndex);
      const next = { ...params };
      switch (wallIndex) {
        case 0:
        case 6:
          next.width = clamp(lengthMm, min.width);
          break;
        case 1:
          next.protrusionOffset = clamp(lengthMm, min.protrusionOffset);
          if (
            next.protrusionOffset + next.protrusionHeight >
            next.height - CLEARANCE_MM
          ) {
            next.height =
              next.protrusionOffset + next.protrusionHeight + CLEARANCE_MM;
          }
          break;
        case 2:
        case 4:
          next.protrusionDepth = clamp(lengthMm, min.protrusionDepth);
          break;
        case 3:
          next.protrusionHeight = clamp(lengthMm, min.protrusionHeight);
          if (
            next.protrusionOffset + next.protrusionHeight >
            next.height - CLEARANCE_MM
          ) {
            next.height =
              next.protrusionOffset + next.protrusionHeight + CLEARANCE_MM;
          }
          break;
        case 5: {
          const topSeg = clamp(lengthMm, CLEARANCE_MM);
          next.height = clamp(
            next.protrusionOffset + next.protrusionHeight + topSeg,
            min.height,
          );
          break;
        }
        case 7:
          next.height = clamp(lengthMm, minLengthMm);
          break;
        default:
          break;
      }
      return finishApply(def, next, wallIndex, lengthMm, minLengthMm);
    },
  },

  slantedWall: {
    id: "slantedWall",
    labelKey: "slantedWall",
    defaultParams: { width: 4500, height: 3000, slant: 1200 },
    minParams: { width: 2000, height: 1500, slant: 400 },
    walls: [
      { index: 0, paramKey: "width", lengthMode: "direct" },
      { index: 1, paramKey: "derived", lengthMode: "derived" },
      { index: 2, paramKey: "derived", lengthMode: "derived" },
      { index: 3, paramKey: "height", lengthMode: "direct" },
    ],
    getVertices: ({ width, height, slant }) => [
      { x: 0, y: 0 },
      { x: width, y: 0 },
      { x: width - slant, y: height },
      { x: 0, y: height },
    ],
    getMinWallLengthMm: (params, wallIndex) => {
      const min = ROOM_SHAPE_DEFINITIONS.slantedWall.minParams;
      switch (wallIndex) {
        case 0:
          return Math.max(min.width, params.slant + CLEARANCE_MM);
        case 1:
          return Math.hypot(CLEARANCE_MM, min.height);
        case 2:
          return CLEARANCE_MM;
        case 3:
          return min.height;
        default:
          return CLEARANCE_MM;
      }
    },
    applyWallLength: (params, wallIndex, lengthMm) => {
      const def = ROOM_SHAPE_DEFINITIONS.slantedWall;
      const min = def.minParams;
      const minLengthMm = def.getMinWallLengthMm(params, wallIndex);
      const next = { ...params };
      switch (wallIndex) {
        case 0:
          next.width = clamp(lengthMm, minLengthMm);
          break;
        case 1: {
          // Diagonal wall: keep height, solve slant from length ≈ hypot(slant, height)
          const slant = Math.sqrt(
            Math.max(0, lengthMm * lengthMm - next.height * next.height),
          );
          next.slant = clamp(slant, min.slant);
          if (next.slant > next.width - CLEARANCE_MM) {
            next.width = next.slant + CLEARANCE_MM;
          }
          break;
        }
        case 2: {
          // Top: keep slant, grow width.
          const top = clamp(lengthMm, CLEARANCE_MM);
          next.width = clamp(top + next.slant, min.width);
          break;
        }
        case 3:
          next.height = clamp(lengthMm, min.height);
          break;
        default:
          break;
      }
      return finishApply(def, next, wallIndex, lengthMm, minLengthMm);
    },
  },

  trapezoid: {
    id: "trapezoid",
    labelKey: "trapezoid",
    defaultParams: { width: 5000, height: 3000, inset: 800 },
    minParams: { width: 2400, height: 1500, inset: 400 },
    walls: [
      { index: 0, paramKey: "width", lengthMode: "direct" },
      { index: 1, paramKey: "derived", lengthMode: "derived" },
      { index: 2, paramKey: "derived", lengthMode: "derived" },
      { index: 3, paramKey: "derived", lengthMode: "derived" },
    ],
    getVertices: ({ width, height, inset }) => [
      { x: 0, y: 0 },
      { x: width, y: 0 },
      { x: width - inset, y: height },
      { x: inset, y: height },
    ],
    getMinWallLengthMm: (params, wallIndex) => {
      const min = ROOM_SHAPE_DEFINITIONS.trapezoid.minParams;
      switch (wallIndex) {
        case 0:
          return Math.max(min.width, params.inset * 2 + CLEARANCE_MM);
        case 1:
        case 3:
          return Math.hypot(min.inset, CLEARANCE_MM);
        case 2:
          return CLEARANCE_MM;
        default:
          return CLEARANCE_MM;
      }
    },
    applyWallLength: (params, wallIndex, lengthMm) => {
      const def = ROOM_SHAPE_DEFINITIONS.trapezoid;
      const min = def.minParams;
      const minLengthMm = def.getMinWallLengthMm(params, wallIndex);
      const next = { ...params };
      switch (wallIndex) {
        case 0:
          next.width = clamp(lengthMm, minLengthMm);
          break;
        case 1:
        case 3: {
          const inset = Math.sqrt(
            Math.max(0, lengthMm * lengthMm - next.height * next.height),
          );
          next.inset = clamp(inset, min.inset);
          const needed = next.inset * 2 + CLEARANCE_MM;
          if (next.width < needed) next.width = needed;
          break;
        }
        case 2: {
          // Top: keep inset, grow width.
          const top = clamp(lengthMm, CLEARANCE_MM);
          next.width = clamp(top + next.inset * 2, min.width);
          break;
        }
        default:
          break;
      }
      return finishApply(def, next, wallIndex, lengthMm, minLengthMm);
    },
  },

  pentagon: {
    id: "pentagon",
    labelKey: "pentagon",
    defaultParams: { width: 4000, height: 4000, peak: 1200 },
    minParams: { width: 2000, height: 2000, peak: 600 },
    walls: [
      { index: 0, paramKey: "width", lengthMode: "direct" },
      { index: 1, paramKey: "derived", lengthMode: "derived" },
      { index: 2, paramKey: "derived", lengthMode: "derived" },
      { index: 3, paramKey: "derived", lengthMode: "derived" },
      { index: 4, paramKey: "derived", lengthMode: "derived" },
    ],
    getVertices: ({ width, height, peak }) => {
      const bodyHeight = Math.max(CLEARANCE_MM, height - peak);
      return [
        { x: 0, y: 0 },
        { x: width, y: 0 },
        { x: width, y: bodyHeight },
        { x: width / 2, y: height },
        { x: 0, y: bodyHeight },
      ];
    },
    getMinWallLengthMm: (params, wallIndex) => {
      const min = ROOM_SHAPE_DEFINITIONS.pentagon.minParams;
      switch (wallIndex) {
        case 0:
          return min.width;
        case 1:
        case 4:
          return CLEARANCE_MM;
        case 2:
        case 3:
          return Math.hypot(min.width / 2, min.peak);
        default:
          return CLEARANCE_MM;
      }
    },
    applyWallLength: (params, wallIndex, lengthMm) => {
      const def = ROOM_SHAPE_DEFINITIONS.pentagon;
      const min = def.minParams;
      const minLengthMm = def.getMinWallLengthMm(params, wallIndex);
      const next = { ...params };
      switch (wallIndex) {
        case 0:
          next.width = clamp(lengthMm, min.width);
          break;
        case 1:
        case 4: {
          const body = clamp(lengthMm, CLEARANCE_MM);
          next.height = clamp(body + next.peak, min.height);
          break;
        }
        case 2:
        case 3: {
          const half = next.width / 2;
          const peak = Math.sqrt(Math.max(0, lengthMm * lengthMm - half * half));
          next.peak = clamp(peak, min.peak);
          if (next.peak > next.height - CLEARANCE_MM) {
            next.height = next.peak + CLEARANCE_MM;
          }
          break;
        }
        default:
          break;
      }
      return finishApply(def, next, wallIndex, lengthMm, minLengthMm);
    },
  },

  alcove: {
    id: "alcove",
    labelKey: "alcove",
    defaultParams: {
      width: 4500,
      height: 3500,
      alcoveWidth: 1800,
      alcoveDepth: 1200,
      alcoveOffset: 1350,
    },
    minParams: {
      width: 2400,
      height: 2000,
      alcoveWidth: 800,
      alcoveDepth: 600,
      alcoveOffset: 0,
    },
    walls: [
      { index: 0, paramKey: "width", lengthMode: "direct" },
      { index: 1, paramKey: "height", lengthMode: "direct" },
      { index: 2, paramKey: "derived", lengthMode: "derived" },
      { index: 3, paramKey: "alcoveDepth", lengthMode: "direct" },
      { index: 4, paramKey: "alcoveWidth", lengthMode: "direct" },
      { index: 5, paramKey: "alcoveDepth", lengthMode: "direct" },
      { index: 6, paramKey: "derived", lengthMode: "derived" },
      { index: 7, paramKey: "height", lengthMode: "direct" },
    ],
    getVertices: ({
      width,
      height,
      alcoveWidth,
      alcoveDepth,
      alcoveOffset,
    }) => [
      { x: 0, y: 0 },
      { x: width, y: 0 },
      { x: width, y: height },
      { x: alcoveOffset + alcoveWidth, y: height },
      { x: alcoveOffset + alcoveWidth, y: height + alcoveDepth },
      { x: alcoveOffset, y: height + alcoveDepth },
      { x: alcoveOffset, y: height },
      { x: 0, y: height },
    ],
    getMinWallLengthMm: (params, wallIndex) => {
      const min = ROOM_SHAPE_DEFINITIONS.alcove.minParams;
      switch (wallIndex) {
        case 0:
          return Math.max(
            min.width,
            params.alcoveOffset + params.alcoveWidth + CLEARANCE_MM,
          );
        case 1:
        case 7:
          return min.height;
        case 2:
        case 6:
          return CLEARANCE_MM;
        case 3:
        case 5:
          return min.alcoveDepth;
        case 4:
          return min.alcoveWidth;
        default:
          return CLEARANCE_MM;
      }
    },
    applyWallLength: (params, wallIndex, lengthMm) => {
      const def = ROOM_SHAPE_DEFINITIONS.alcove;
      const min = def.minParams;
      const minLengthMm = def.getMinWallLengthMm(params, wallIndex);
      const next = { ...params };
      switch (wallIndex) {
        case 0:
          next.width = clamp(lengthMm, minLengthMm);
          break;
        case 1:
        case 7:
          next.height = clamp(lengthMm, min.height);
          break;
        case 2: {
          // Right top segment: keep alcove size/offset, grow width.
          const right = clamp(lengthMm, CLEARANCE_MM);
          next.width = clamp(
            next.alcoveOffset + next.alcoveWidth + right,
            min.width,
          );
          break;
        }
        case 3:
        case 5:
          next.alcoveDepth = clamp(lengthMm, min.alcoveDepth);
          break;
        case 4:
          next.alcoveWidth = clamp(lengthMm, min.alcoveWidth);
          if (next.alcoveOffset + next.alcoveWidth > next.width - CLEARANCE_MM) {
            next.width = next.alcoveOffset + next.alcoveWidth + CLEARANCE_MM;
          }
          break;
        case 6: {
          // Left top segment: set offset, grow width if alcove no longer fits.
          const left = clamp(lengthMm, CLEARANCE_MM);
          next.alcoveOffset = left;
          if (next.alcoveOffset + next.alcoveWidth > next.width - CLEARANCE_MM) {
            next.width = next.alcoveOffset + next.alcoveWidth + CLEARANCE_MM;
          }
          break;
        }
        default:
          break;
      }
      return finishApply(def, next, wallIndex, lengthMm, minLengthMm);
    },
  },

  inset: {
    id: "inset",
    labelKey: "inset",
    defaultParams: {
      width: 4500,
      height: 3500,
      insetWidth: 1800,
      insetDepth: 1200,
      insetOffset: 1350,
    },
    minParams: {
      width: 2400,
      height: 2000,
      insetWidth: 800,
      insetDepth: 600,
      insetOffset: 0,
    },
    walls: [
      { index: 0, paramKey: "width", lengthMode: "direct" },
      { index: 1, paramKey: "height", lengthMode: "direct" },
      { index: 2, paramKey: "derived", lengthMode: "derived" },
      { index: 3, paramKey: "insetDepth", lengthMode: "direct" },
      { index: 4, paramKey: "insetWidth", lengthMode: "direct" },
      { index: 5, paramKey: "insetDepth", lengthMode: "direct" },
      { index: 6, paramKey: "derived", lengthMode: "derived" },
      { index: 7, paramKey: "height", lengthMode: "direct" },
    ],
    getVertices: ({ width, height, insetWidth, insetDepth, insetOffset }) => [
      { x: 0, y: 0 },
      { x: width, y: 0 },
      { x: width, y: height },
      { x: insetOffset + insetWidth, y: height },
      { x: insetOffset + insetWidth, y: height - insetDepth },
      { x: insetOffset, y: height - insetDepth },
      { x: insetOffset, y: height },
      { x: 0, y: height },
    ],
    getMinWallLengthMm: (params, wallIndex) => {
      const min = ROOM_SHAPE_DEFINITIONS.inset.minParams;
      switch (wallIndex) {
        case 0:
          return Math.max(
            min.width,
            params.insetOffset + params.insetWidth + CLEARANCE_MM,
          );
        case 1:
        case 7:
          return Math.max(min.height, params.insetDepth + CLEARANCE_MM);
        case 2:
        case 6:
          return CLEARANCE_MM;
        case 3:
        case 5:
          return min.insetDepth;
        case 4:
          return min.insetWidth;
        default:
          return CLEARANCE_MM;
      }
    },
    applyWallLength: (params, wallIndex, lengthMm) => {
      const def = ROOM_SHAPE_DEFINITIONS.inset;
      const min = def.minParams;
      const minLengthMm = def.getMinWallLengthMm(params, wallIndex);
      const next = { ...params };
      switch (wallIndex) {
        case 0:
          next.width = clamp(lengthMm, minLengthMm);
          break;
        case 1:
        case 7:
          next.height = clamp(lengthMm, minLengthMm);
          break;
        case 2: {
          // Right top segment: keep inset size/offset, grow width.
          const right = clamp(lengthMm, CLEARANCE_MM);
          next.width = clamp(
            next.insetOffset + next.insetWidth + right,
            min.width,
          );
          break;
        }
        case 3:
        case 5:
          next.insetDepth = clamp(lengthMm, min.insetDepth);
          if (next.insetDepth > next.height - CLEARANCE_MM) {
            next.height = next.insetDepth + CLEARANCE_MM;
          }
          break;
        case 4:
          next.insetWidth = clamp(lengthMm, min.insetWidth);
          if (next.insetOffset + next.insetWidth > next.width - CLEARANCE_MM) {
            next.width = next.insetOffset + next.insetWidth + CLEARANCE_MM;
          }
          break;
        case 6: {
          // Left top segment: set offset, grow width if inset no longer fits.
          const left = clamp(lengthMm, CLEARANCE_MM);
          next.insetOffset = left;
          if (next.insetOffset + next.insetWidth > next.width - CLEARANCE_MM) {
            next.width = next.insetOffset + next.insetWidth + CLEARANCE_MM;
          }
          break;
        }
        default:
          break;
      }
      return finishApply(def, next, wallIndex, lengthMm, minLengthMm);
    },
  },
};

export const getRoomShapeDefinition = (id: RoomShapeId): RoomShapeDefinition =>
  ROOM_SHAPE_DEFINITIONS[id];

/**
 * Convert mm polygon vertices to world-unit (meter) coordinates, centered on origin.
 */
export const verticesMmToWorldCentered = (
  verticesMm: Array<{ x: number; y: number }>,
): Array<{ x: number; y: number }> => {
  const xs = verticesMm.map((v) => v.x);
  const ys = verticesMm.map((v) => v.y);
  const midX = (Math.min(...xs) + Math.max(...xs)) / 2;
  const midY = (Math.min(...ys) + Math.max(...ys)) / 2;
  return verticesMm.map((v) => ({
    x: (v.x - midX) / 1000,
    y: (v.y - midY) / 1000,
  }));
};

/** @deprecated Use verticesMmToWorldCentered */
export const verticesCmToWorldCentered = verticesMmToWorldCentered;
